
import { defineComponent, ref, computed, watch, onMounted, h } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { ElNotification } from "element-plus";
import { api } from "@/apis/user";

const DESKTOP_NOTICE_PREVIEW_MAX = 600;
const LAST_NOTICE_PREVIEW_KEY = "navbar_notice_last_preview";

function normalizeNotice(res: any): string | null {
  const body = res?.data;
  if (!body || (body.status !== 200 && body.status !== 204)) return null;
  const d = body.data;
  if (d == null || d === "") return null;
  if (typeof d === "string") {
    const s = d.trim();
    return s || null;
  }
  if (Array.isArray(d)) {
    const parts = d.map((item: any) => {
      if (typeof item === "string") return item;
      if (item?.title != null && item?.content != null) {
        return `${item.title}\n${item.content}`;
      }
      if (item?.title != null) return String(item.title);
      if (item?.content != null) return String(item.content);
      return "";
    });
    const joined = parts.filter(Boolean).join("\n\n");
    return joined || null;
  }
  if (typeof d === "object") {
    if (typeof d.content === "string" && d.content.trim()) return d.content.trim();
    if (typeof d.title === "string" && d.title.trim()) return d.title.trim();
  }
  return String(d);
}

export default defineComponent({
  name: "NavbarNotice",
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const noticeText = ref<string | null>(null);
    const loading = ref(false);
    const error = ref(false);
    const badgeDismissed = ref(false);
    const desktopTipShown = ref(false);

    const displayCount = computed(() => {
      if (!noticeText.value || badgeDismissed.value) return 0;
      return 1;
    });

    const fetchNotice = async (opts?: { showDesktop?: boolean }) => {
      loading.value = true;
      error.value = false;
      try {
        const res: any = await api.detail.infoNoticeGet();
        const text = normalizeNotice(res);
        noticeText.value = text;
        if (text) {
          badgeDismissed.value = false;
          const noticeFingerprint = text.trim();
          const lastPreviewed = sessionStorage.getItem(LAST_NOTICE_PREVIEW_KEY);
          if (
            opts?.showDesktop &&
            !desktopTipShown.value &&
            noticeFingerprint &&
            lastPreviewed !== noticeFingerprint
          ) {
            desktopTipShown.value = true;
            sessionStorage.setItem(LAST_NOTICE_PREVIEW_KEY, noticeFingerprint);
            const msg =
              text.length > DESKTOP_NOTICE_PREVIEW_MAX
                ? `${text.slice(0, DESKTOP_NOTICE_PREVIEW_MAX)}…`
                : text;
            ElNotification({
              title: t("navbar.announcement"),
              message: h("div", { class: "navbar-notice-toast" }, msg),
              duration: 6000,
              showClose: true,
              type: "info"
            });
          }
        }
      } catch {
        error.value = true;
        noticeText.value = null;
      } finally {
        loading.value = false;
      }
    };

    const onPopoverShow = () => {
      void fetchNotice();
    };

    const markSeen = () => {
      badgeDismissed.value = true;
    };

    onMounted(() => {
      void fetchNotice({ showDesktop: true });
    });

    watch(
      () => route.path,
      path => {
        if (path.includes("dashboard")) {
          void fetchNotice();
        }
      }
    );

    return {
      t,
      noticeText,
      loading,
      error,
      displayCount,
      onPopoverShow,
      markSeen
    };
  }
});
